import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  Globe,
  LayoutDashboard,
  Palette,
  Percent,
  ScrollText,
  Shield,
  ShieldCheck,
  Ticket,
  UserRoundCheck,
  Users,
  type LucideIcon,
} from 'lucide-react';

import { AppShellFrame } from '@/shared/ui/AppShellFrame';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/shared/ui/components/sidebar';
import { SidebarAccountMenu } from '@/modules/auth/SidebarAccountMenu';
import { useAuth } from '@/modules/auth/AuthContext';
import { useAuthorization, type Authorizer } from '@/modules/auth/useAuthorization';
import { useUnresolvedVenueAccessSummary } from '@/modules/users/api';

type NavItem = {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  /** Permission codes that reveal the item; omit for always-visible. Any match wins. */
  permissions?: string[];
};

type NavSection = {
  /** i18n key for the section header; omit for an unlabeled top group. */
  headerKey?: string;
  items: NavItem[];
};

const SECTIONS: NavSection[] = [
  {
    headerKey: 'nav.administration',
    items: [
      { to: '/admin/users', labelKey: 'nav.users', icon: Users, permissions: ['iam.user.manage'] },
      { to: '/admin/roles', labelKey: 'nav.roles', icon: Shield, permissions: ['iam.role.manage'] },
      { to: '/admin/audit', labelKey: 'nav.audit', icon: ScrollText, permissions: ['audit.read'] },
    ],
  },
  {
    headerKey: 'nav.franchise',
    items: [
      {
        to: '/admin/brand',
        labelKey: 'nav.brand',
        icon: Palette,
        permissions: ['franchise.brand.manage'],
      },
      {
        to: '/admin/country-policies',
        labelKey: 'nav.countryPolicies',
        icon: Globe,
        permissions: ['franchise.country.manage'],
      },
      {
        to: '/admin/venues',
        labelKey: 'nav.venues',
        icon: Building2,
        permissions: ['venue.view', 'venue.manage'],
      },
      {
        to: '/admin/waivers',
        labelKey: 'nav.waivers',
        icon: ShieldCheck,
        permissions: ['venue.waiver_template.manage'],
      },
    ],
  },
  {
    headerKey: 'nav.products',
    items: [
      {
        to: '/admin/products',
        labelKey: 'nav.passes',
        icon: Ticket,
        permissions: ['products.pass.view', 'products.pass.manage'],
      },
      {
        to: '/admin/discounts',
        labelKey: 'nav.discounts',
        icon: Percent,
        permissions: ['discount.manage'],
      },
    ],
  },
  {
    headerKey: 'nav.operations',
    items: [
      {
        to: '/admin/employees',
        labelKey: 'nav.employees',
        icon: UserRoundCheck,
        permissions: ['workforce.employee.view', 'workforce.employee.manage'],
      },
    ],
  },
];

/**
 * The Franchise admin shell wrapping `/admin/...` (see routes.tsx). A sibling of the Venue
 * workspace `VenueShell`, never nested under it (ADR 0015): it carries the franchise nav,
 * has *no* Venue switcher (these features are franchise-wide), and offers a return-to-workspace
 * control back to the landing resolver. Nav is config-driven — each item is gated on its
 * franchise-wide permission, and a section header hides when none of its items are visible.
 *
 * Chrome (floating collapsible sidebar + primary header band) comes from {@link AppShellFrame}.
 */
export function AdminShell() {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const auth = useAuthorization();
  const { pathname } = useLocation();

  const canViewAssignments = auth.can('role_assignment.view');
  const unresolved = useUnresolvedVenueAccessSummary({ enabled: canViewAssignments });
  const usersNeedAttention = (unresolved.data?.unresolvedAssignmentCount ?? 0) > 0;

  const sections = SECTIONS.map((section) => ({
    headerKey: section.headerKey,
    items: section.items.filter((item) => isVisible(item, auth)),
  })).filter((section) => section.items.length > 0);

  const isActive = (to: string) => pathname === to || pathname.startsWith(`${to}/`);

  return (
    <AppShellFrame
      title={t('admin.sectionHeader')}
      subtitle={t('admin.homeBody')}
      navLabel={t('admin.sectionHeader')}
      sidebarHeader={
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" className="gap-2.5 [&>svg]:size-6" asChild>
              <Link to="/admin">
                <LayoutDashboard className="text-primary" />
                <span className="text-base font-semibold">{t('app.name')}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      }
      sidebarContent={sections.map((section, index) => (
        <SidebarGroup key={section.headerKey ?? `top-${index}`}>
          {section.headerKey && <SidebarGroupLabel>{t(section.headerKey)}</SidebarGroupLabel>}
          <SidebarGroupContent>
            <SidebarMenu>
              {section.items.map((item) => {
                const Icon = item.icon;
                const needsAttention = item.to === '/admin/users' && usersNeedAttention;
                return (
                  <SidebarMenuItem key={item.to}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.to)}
                      tooltip={t(item.labelKey)}
                    >
                      <Link to={item.to}>
                        <Icon aria-hidden="true" />
                        <span>{t(item.labelKey)}</span>
                      </Link>
                    </SidebarMenuButton>
                    {needsAttention && (
                      <SidebarMenuBadge>
                        <AlertTriangle
                          className="size-4 text-amber-500"
                          aria-label={t('nav.usersNeedVenueAccess')}
                        />
                      </SidebarMenuBadge>
                    )}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      ))}
      sidebarFooter={
        <>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild tooltip={t('admin.returnToWorkspace')}>
                <Link to="/">
                  <ArrowLeft aria-hidden="true" />
                  <span>{t('admin.returnToWorkspace')}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
          {user && <SidebarAccountMenu name={user.name} email={user.email} />}
        </>
      }
    />
  );
}

function isVisible(item: NavItem, auth: Authorizer): boolean {
  if (!item.permissions) return true;
  return auth.canAny(item.permissions);
}
